import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '@/app/hooks';
import { L } from '@/i18n';

/**
 * RPG "system" layer: game-style system windows ("[ SYSTEM ] QUEST COMPLETE"),
 * decoding headings and typewriter text. Everything falls back to plain,
 * instant text when motion is reduced or off.
 */

export interface SystemMsg {
  kind: 'quest' | 'level' | 'mastery' | 'unlock' | 'achievement' | 'boss' | 'daily' | 'account' | 'info';
  /** Short headline, e.g. "QUEST COMPLETE". */
  title: string;
  /** Typed out one after another. */
  lines: string[];
  /** Reward badge, e.g. "+40 XP". */
  reward?: string;
}

type Fn = (m: SystemMsg) => void;
const listeners = new Set<Fn>();

/** Show a system window (queued; one at a time). */
export function systemNotify(m: SystemMsg) {
  listeners.forEach((fn) => fn(m));
}

/* ------------------------------------------------------------------ */
/* Decode / typewriter                                                  */
/* ------------------------------------------------------------------ */

const GLYPHS = 'ABCDEFGHJKLMNPQRSTUVWXYZÆØÅ0123456789#$%&*+=<>/';

/** Text that "decodes" from random glyphs into the real text, left to right. */
export function DecodeText({ text, duration = 520, className }: { text: string; duration?: number; className?: string }) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(reduced ? text : '');
  useEffect(() => {
    if (reduced || !text) {
      setShown(text);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / duration);
      const fixed = Math.floor(k * text.length);
      let out = text.slice(0, fixed);
      for (let i = fixed; i < text.length; i++) out += text[i] === ' ' ? ' ' : GLYPHS[(Math.random() * GLYPHS.length) | 0];
      setShown(out);
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, duration, reduced]);
  return (
    <span className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">{shown || ' '}</span>
    </span>
  );
}

/** Lines typed one after another; calls onDone when everything is visible. */
export function TypeLines({ lines, onDone, speed = 18 }: { lines: string[]; onDone?: () => void; speed?: number }) {
  const reduced = useReducedMotion();
  const total = lines.join('').length;
  const [n, setN] = useState(reduced ? total : 0);
  const done = useRef(false);
  useEffect(() => {
    if (reduced) {
      setN(total);
      return;
    }
    const id = setInterval(() => setN((x) => (x >= total ? x : x + 1)), speed);
    return () => clearInterval(id);
  }, [total, speed, reduced]);
  useEffect(() => {
    if (n >= total && !done.current) {
      done.current = true;
      onDone?.();
    }
  }, [n, total, onDone]);
  let left = n;
  return (
    <div className="sysw-lines" aria-hidden="true">
      {lines.map((line, i) => {
        const part = line.slice(0, Math.max(0, left));
        left -= line.length;
        const typing = part.length > 0 && part.length < line.length;
        return part ? <p key={i} className={typing ? 'typing' : ''}>{part}</p> : null;
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* System window queue                                                  */
/* ------------------------------------------------------------------ */

function kindLabel(kind: SystemMsg['kind']) {
  switch (kind) {
    case 'quest': return L('QUEST', 'OPPDRAG');
    case 'level': return L('LEVEL', 'NIVÅ');
    case 'mastery': return L('MASTERY', 'MESTRING');
    case 'unlock': return L('UNLOCKED', 'LÅST OPP');
    case 'achievement': return L('ACHIEVEMENT', 'PRESTASJON');
    case 'boss': return L('BOSS', 'BOSS');
    case 'daily': return L('DAILY', 'DAGLIG');
    case 'account': return L('ACCOUNT', 'KONTO');
    default: return L('NOTICE', 'MELDING');
  }
}

function SystemWindow({ msg, onClose, hurry }: { msg: SystemMsg; onClose: () => void; hurry: boolean }) {
  const [closing, setClosing] = useState(false);
  const close = useCallback(() => setClosing(true), []);
  const typed = useCallback(() => {
    // Stay long enough to read, shorter when more windows are waiting.
    const words = (msg.title + msg.lines.join(' ')).split(/\s+/).length;
    setTimeout(close, hurry ? 1400 : Math.min(5200, 1800 + words * 160));
  }, [msg, hurry, close]);
  useEffect(() => {
    if (!closing) return;
    const id = setTimeout(onClose, 260);
    return () => clearTimeout(id);
  }, [closing, onClose]);
  return (
    <div className={`sysw ${msg.kind} ${closing ? 'closing' : ''}`} role="status" aria-live="polite" onClick={close}>
      <span className="sysw-corner tl" /><span className="sysw-corner tr" /><span className="sysw-corner bl" /><span className="sysw-corner br" />
      <div className="sysw-head">
        <span className="sysw-sys">{L('[ SYSTEM ]', '[ SYSTEM ]')}</span>
        <span className="sysw-kind">{kindLabel(msg.kind)}</span>
      </div>
      <div className="sysw-title"><DecodeText text={msg.title} duration={420} /></div>
      <span className="sr-only">{msg.lines.join(' ')} {msg.reward ?? ''}</span>
      <TypeLines lines={msg.lines} onDone={typed} speed={hurry ? 8 : 16} />
      {msg.reward && <div className="sysw-reward">{msg.reward}</div>}
    </div>
  );
}

/** Mount once at app level. */
export function SystemLayer() {
  const [queue, setQueue] = useState<(SystemMsg & { id: number })[]>([]);
  const counter = useRef(0);
  useEffect(() => {
    const fn: Fn = (m) => setQueue((q) => [...q.slice(-5), { ...m, id: ++counter.current }]);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  const current = queue[0];
  const next = useCallback(() => setQueue((q) => q.slice(1)), []);
  if (!current) return null;
  return (
    <div className="sysw-layer">
      <SystemWindow key={current.id} msg={current} onClose={next} hurry={queue.length > 1} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Combo counter                                                        */
/* ------------------------------------------------------------------ */

/** "COMBO ×3" pop; re-animates on every increase, fades away after a moment. */
export function ComboPop({ combo }: { combo: number }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (combo < 2) {
      setVisible(false);
      return;
    }
    setVisible(true);
    const id = setTimeout(() => setVisible(false), 1500);
    return () => clearTimeout(id);
  }, [combo]);
  if (!visible) return null;
  const big = combo >= 5;
  return (
    <div key={combo} className={`combo-pop ${big ? 'big' : ''}`} aria-live="polite">
      <span className="combo-k">{big ? L('STREAK!', 'REKKE!') : L('COMBO', 'KOMBO')}</span>
      <span className="combo-n">×{combo}</span>
    </div>
  );
}
